/**
 * @file Loads and mutates an organization's distribution schedules through React Query.
 */
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import {
  createScheduleOperation,
  deleteScheduleOperation,
  listSchedulesOperation,
  updateScheduleOperation,
} from "@api/services/transactions";
import type {
  CreateScheduleInputMutation,
  CreateScheduleOutput,
  UpdateScheduleInputMutation,
  UpdateScheduleOutput,
  ListSchedulesOutput,
  Schedules,
} from "@api/types";
import {
  ACTIVE as SCHEDULE_STATUS_ACTIVE,
  HOLD as SCHEDULE_STATUS_HOLD,
} from "@api/services/transactions/shapes/ScheduleStatusEnum";

const STALE_DELAY_MS = 2 * 60 * 1000;
const INDEX_QUERY_KEY = "schedules";

type Options = {
  isEnabled?: boolean;
};

/**
 * Loads the organization's distribution schedules and exposes the actions to create, update,
 * delete, enable and disable them. The `isEnabled` option stops the load for a user that
 * cannot read the schedules.
 */
const useSchedulesStore = ({ isEnabled = true }: Options = {}) => {
  const { request, isAuthorized, getOrganizationId } = useAppContext() as AppContext;
  const queryClient = useQueryClient();

  const organizationId = getOrganizationId();
  const queryKey = useMemo(() => [ organizationId, INDEX_QUERY_KEY ], [ organizationId ]);

  const { data: schedules, isLoading } = useQuery<Schedules>({
    staleTime: STALE_DELAY_MS,
    /**
     * Fetches the organization's schedules.
     */
    queryFn: async () => {
      const { data } = await request<ListSchedulesOutput>(listSchedulesOperation);
      return data;
    },
    enabled: isAuthorized && isEnabled,
    queryKey,
  });

  const getProjectSchedule = useCallback(
    (projectId: string) => schedules?.find((schedule) => schedule.projectId === projectId),
    [ schedules ],
  );

  const createSchedule = useCallback(
    async (mutation: CreateScheduleInputMutation) => {
      const { data } = await request<CreateScheduleOutput>(createScheduleOperation, { mutation });

      queryClient.setQueryData<Schedules>(queryKey, (prev) =>
        prev ? [ ...prev, data ] : [ data ],
      );

      return data;
    },
    [ request, queryClient, queryKey ],
  );

  const updateSchedule = useCallback(
    async (scheduleId: string, mutation: UpdateScheduleInputMutation) => {
      const { data } = await request<UpdateScheduleOutput>(updateScheduleOperation, { id: scheduleId, mutation });

      queryClient.setQueryData<Schedules>(queryKey, (prev) =>
        prev?.map((schedule) => schedule.id === scheduleId ? data : schedule),
      );

      return data;
    },
    [ request, queryClient, queryKey ],
  );

  const deleteSchedule = useCallback(
    async (scheduleId: string) => {
      await request(deleteScheduleOperation, { id: scheduleId });

      queryClient.setQueryData<Schedules>(queryKey, (prev) =>
        prev?.filter((schedule) => schedule.id !== scheduleId),
      );
    },
    [ request, queryClient, queryKey ],
  );

  const enableSchedule = useCallback(
    async (scheduleId: string) => updateSchedule(scheduleId, { status: SCHEDULE_STATUS_ACTIVE }),
    [ updateSchedule ],
  );

  const disableSchedule = useCallback(
    async (scheduleId: string) => updateSchedule(scheduleId, { status: SCHEDULE_STATUS_HOLD }),
    [ updateSchedule ],
  );

  return {
    isLoading,
    schedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    getProjectSchedule,
    enableSchedule,
    disableSchedule,
  };
};

export default useSchedulesStore;
